import { useState, type FormEvent } from 'react';
import { addDoc, collection, doc, increment, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface Props {
  checklistId: string;
  nextOrder: number;
}

export default function AddItemForm({ checklistId, nextOrder }: Props) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [acceptanceCriteria, setAcceptanceCriteria] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'checklists', checklistId, 'items'), {
        order: nextOrder,
        description: description.trim(),
        acceptanceCriteria: acceptanceCriteria.trim() || null,
        status: 'pending',
        qcNotes: null,
        screenshotUrl: null,
        completedBy: null,
        completedAt: null,
        createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, 'checklists', checklistId), {
        itemCount: increment(1),
        pendingCount: increment(1),
        updatedAt: serverTimestamp()
      });
      setDescription('');
      setAcceptanceCriteria('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add item');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <button className="add-item-trigger" onClick={() => setOpen(true)}>
        + Add item
      </button>
    );
  }

  return (
    <form className="add-item-form" onSubmit={handleSubmit}>
      <label className="field">
        <span className="field__label">Item to verify</span>
        <input
          type="text"
          required
          autoFocus
          placeholder="e.g. Push-to-View modal uses PP-teal theme"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">Expected result / acceptance criteria (optional)</span>
        <textarea
          rows={2}
          placeholder="e.g. Modal background is #08AC91, white text, no white background flash on open"
          value={acceptanceCriteria}
          onChange={(e) => setAcceptanceCriteria(e.target.value)}
        />
      </label>

      {error && <div className="form-error">{error}</div>}

      <div className="add-item-form__actions">
        <button type="button" className="btn btn--ghost" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button type="submit" className="btn btn--primary" disabled={submitting || !description.trim()}>
          {submitting ? 'Adding…' : 'Add item'}
        </button>
      </div>
    </form>
  );
}
